import { useState, useEffect, useCallback } from 'react';
import type { DomainConfig, SenderAccount } from '../layout/AccountSwitcher';
import { getRegionDomain, getCommonRegion, getRegionLabel } from '../../utils/regionMap';
import { getCountryLanguage, isEnglishCountry, getLanguagesForCountries } from '../../utils/countryLanguage';
import { authFetch } from '../../utils/authFetch';
import * as XLSX from 'xlsx';

interface Lead {
    id: string;
    campaignId: string;
    name: string;
    title: string;
    companyName: string;
    email: string;
    country: string;
    status: string;
    emailsSentCount: number;
    sourceUrl?: string;
}

interface Campaign {
    id: string;
    name: string;
}

interface Thread {
    id: string;
    direction: 'inbound' | 'outbound';
    fromEmail: string;
    toEmail: string;
    subject: string;
    bodyText: string;
    receivedAt: string;
}

const REGION_ICONS: Record<string, string> = {
    ASIA: '🌏', EU: '🇪🇺', LAT: '🌎',
    US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', AR: '🇦🇷', UY: '🇺🇾', BR: '🇧🇷',
    CO: '🇨🇴', CL: '🇨🇱', ES: '🇪🇸', FR: '🇫🇷', DE: '🇩🇪', GB: '🇬🇧',
    AE: '🇦🇪', SG: '🇸🇬', AU: '🇦🇺',
};

const PLACEHOLDERS = ['{{name}}', '{{company}}', '{{title}}', '{{country}}', '{{senderName}}', '{{senderEmail}}'];

function renderTemplate(template: string, vars: Record<string, string>): string {
    let result = template;
    for (const [key, val] of Object.entries(vars)) {
        result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val);
    }
    return result;
}

interface Props {
    accounts: DomainConfig[];
}

export function ContactsPage({ accounts }: Props) {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [countryFilter, setCountryFilter] = useState('all');
    const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
    const [threads, setThreads] = useState<Thread[]>([]);

    // ── Multi-select & Auto-send ──
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showCompose, setShowCompose] = useState(false);
    const [fromEmail, setFromEmail] = useState(accounts[0]?.senders[0]?.email || '');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [sending, setSending] = useState(false);
    const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });

    const allSenders = accounts.flatMap(d => d.senders);

    // ── Region-locked sending ──
    // Determine which region the selected contacts belong to
    const selectedLeadsForRegion = leads.filter(l => selectedIds.has(l.id));
    const selectedCountries = selectedLeadsForRegion.map(l => l.country);
    const commonRegion = getCommonRegion(selectedCountries); // e.g. "lawhelper.asia"
    const isMixedRegion = selectedCountries.length > 0 && !commonRegion;

    // Only show senders from the matching region
    const allowedSenders = commonRegion
        ? allSenders.filter(s => s.domain === commonRegion)
        : allSenders;

    // Auto-select the first allowed sender when region changes
    const currentFromValid = allowedSenders.some(s => s.email === fromEmail);
    const effectiveFromEmail = currentFromValid ? fromEmail : (allowedSenders[0]?.email || '');
    const sender = allSenders.find(s => s.email === effectiveFromEmail);

    const load = useCallback(async () => {
        try {
            const res = await authFetch('/campaigns');
            const data = await res.json();
            const campaignList = data.campaigns || [];
            setCampaigns(campaignList);
            const all: Lead[] = [];
            for (const c of campaignList) {
                try {
                    const lr = await authFetch(`/campaigns/${c.id}`);
                    const ld = await lr.json();
                    
                    // The API returns the leads array wrapper or directly the array depending on the endpoint version 
                    const leadsArray = Array.isArray(ld) ? ld : (ld.leads || []);
                    all.push(...leadsArray);
                } catch { }
            }
            // ── Dedup by email: keep the most recent (last in array) ──
            const emailMap = new Map<string, Lead>();
            for (const lead of all) {
                if (!lead || !lead.email) continue;
                const key = lead.email.toLowerCase();
                emailMap.set(key, lead); // later entries overwrite earlier = keep latest
            }
            const deduped = Array.from(emailMap.values());
            setLeads(deduped);
            if (all.length !== deduped.length) {
                console.log(`[Contacts] Deduped: ${all.length} → ${deduped.length} (${all.length - deduped.length} duplicates removed)`);
            }
        } catch { }
    }, []);

    useEffect(() => { load(); }, [load]);

    const selectLead = async (lead: Lead) => {
        setSelectedLead(lead);
        try {
            const res = await authFetch(`/leads/${lead.id}/threads`);
            const data = await res.json();
            setThreads(data.threads || []);
        } catch { setThreads([]); }
    };

    const countries = Array.from(new Set(leads.map(l => l.country))).sort();
    const statuses = Array.from(new Set(leads.map(l => l.status))).sort();

    const filtered = leads.filter(l => {
        if (statusFilter !== 'all' && l.status !== statusFilter) return false;
        if (countryFilter !== 'all' && l.country !== countryFilter) return false;
        if (search) {
            const s = search.toLowerCase();
            return l.name.toLowerCase().includes(s)
                || l.companyName.toLowerCase().includes(s)
                || l.email.toLowerCase().includes(s)
                || l.title.toLowerCase().includes(s);
        }
        return true;
    });

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        const sendable = filtered.filter(l => !['invalid', 'review', 'bounced'].includes(l.status));
        if (selectedIds.size === sendable.length && sendable.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(sendable.map(l => l.id)));
        }
    };

    const downloadExcel = () => {
        const exportData = filtered.map(l => ({
            'Name': l.name,
            'Title': l.title,
            'Company': l.companyName,
            'Email': l.email,
            'Country': l.country,
            'Status': l.status,
            'Emails Sent': l.emailsSentCount,
            'Campaign': campaigns.find(c => c.id === l.campaignId)?.name || '',
            'Source URL': l.sourceUrl || '',
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        // Auto-size columns
        const colWidths = Object.keys(exportData[0] || {}).map(key => ({
            wch: Math.max(key.length, ...exportData.map(r => String((r as any)[key] || '').length).slice(0, 100)) + 2
        }));
        ws['!cols'] = colWidths;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
        const fileName = `contacts_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const selectedLeads = leads.filter(l => selectedIds.has(l.id));

    const getPreviewVars = (lead: Lead) => ({
        name: lead.name,
        company: lead.companyName,
        title: lead.title,
        country: lead.country,
        senderName: sender?.displayName || '',
        senderEmail: sender?.email || '',
    });

    const autoSend = async () => {
        if (!effectiveFromEmail || selectedLeads.length === 0 || !emailSubject || !emailBody || isMixedRegion) return;
        setSending(true);
        setSendProgress({ sent: 0, total: selectedLeads.length });

        for (let i = 0; i < selectedLeads.length; i++) {
            const lead = selectedLeads[i];
            const vars = getPreviewVars(lead);
            try {
                await authFetch('/emails/reply', {
                    method: 'POST',
                    body: JSON.stringify({
                        leadId: lead.id,
                        toEmail: lead.email,
                        fromEmail: effectiveFromEmail,
                        subject: renderTemplate(emailSubject, vars),
                        bodyText: renderTemplate(emailBody, vars),
                    }),
                });
            } catch (e) {
                console.error(`Failed to send to ${lead.email}:`, e);
            }
            setSendProgress({ sent: i + 1, total: selectedLeads.length });
        }

        setSending(false);
        setShowCompose(false);
        setSelectedIds(new Set());
        setEmailSubject('');
        setEmailBody('');
        load();
    };

    // ── Update lead status (approve/reject review leads) ──
    const updateLeadStatus = async (leadId: string, newStatus: string) => {
        try {
            await authFetch(`/leads/${leadId}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status: newStatus }),
            });
            // Update local state immediately for responsiveness
            setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
            if (selectedLead?.id === leadId) {
                setSelectedLead(prev => prev ? { ...prev, status: newStatus } : null);
            }
        } catch (e) {
            console.error(`Failed to update lead ${leadId} to ${newStatus}:`, e);
        }
    };

    const bulkUpdateStatus = async (ids: string[], newStatus: string) => {
        for (const id of ids) {
            await updateLeadStatus(id, newStatus);
        }
    };

    const reviewLeadsInView = filtered.filter(l => l.status === 'review');

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <h2 className="page-title">👤 Contacts</h2>
                    <p className="page-subtitle">{leads.length} contacts across {campaigns.length} campaigns</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Bulk review actions */}
                    {reviewLeadsInView.length > 0 && (
                        <div style={{
                            display: 'flex', gap: 6, alignItems: 'center',
                            padding: '6px 12px', borderRadius: 'var(--radius-md)',
                            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
                        }}>
                            <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                                🔶 {reviewLeadsInView.length} needs review
                            </span>
                            <button
                                className="btn btn-sm"
                                style={{ background: '#22c55e', color: '#fff', fontSize: 11, padding: '4px 10px' }}
                                onClick={() => bulkUpdateStatus(reviewLeadsInView.map(l => l.id), 'discovered')}
                            >
                                ✓ Approve All
                            </button>
                            <button
                                className="btn btn-sm"
                                style={{ background: '#7f1d1d', color: '#fca5a5', fontSize: 11, padding: '4px 10px' }}
                                onClick={() => bulkUpdateStatus(reviewLeadsInView.map(l => l.id), 'invalid')}
                            >
                                ✗ Reject All
                            </button>
                        </div>
                    )}
                    {selectedIds.size > 0 && (
                        <>
                            {isMixedRegion && (
                                <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>
                                    ⚠️ Mixed regions — select contacts from one region only
                                </span>
                            )}
                            {commonRegion && (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                    {getRegionLabel(commonRegion)}
                                </span>
                            )}
                            <button className="btn btn-primary" onClick={() => setShowCompose(true)}
                                disabled={isMixedRegion}>
                                ✉️ Send Email to {selectedIds.size} Contact{selectedIds.size > 1 ? 's' : ''}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="input" style={{ maxWidth: 280 }} placeholder="🔍 Search name, company, email..."
                    value={search} onChange={(e) => setSearch(e.target.value)} />

                <select className="input" style={{ width: 'auto' }} value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">All statuses</option>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>

                <select className="input" style={{ width: 'auto' }} value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}>
                    <option value="all">All countries</option>
                    {countries.map(c => <option key={c} value={c}>{REGION_ICONS[c] || '🌍'} {c}</option>)}
                </select>

                <span style={{ padding: '9px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
                    {filtered.length} results
                    {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
                </span>

                <button className="btn btn-outline" onClick={downloadExcel}
                    disabled={filtered.length === 0}
                    style={{ marginLeft: 'auto', fontSize: 12 }}>
                    📥 Export Excel ({filtered.length})
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: selectedLead ? '1fr 400px' : '1fr', gap: 16 }}>
                {/* Table */}
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input type="checkbox" className="checkbox"
                                            checked={filtered.length > 0 && selectedIds.size === filtered.length}
                                            onChange={toggleAll} />
                                    </th>
                                    <th>Name</th>
                                    <th>Title</th>
                                    <th>Company</th>
                                    <th>Email</th>
                                    <th>Country</th>
                                    <th>Language</th>
                                    <th>Campaign</th>
                                    <th>Status</th>
                                    <th>Emails</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>No contacts found</td></tr>
                                ) : (
                                    filtered.map(l => (
                                        <tr key={l.id}
                                            className={selectedLead?.id === l.id ? 'selected' : ''}
                                            style={{ cursor: 'pointer' }}>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                {l.status === 'review' ? (
                                                    <div style={{ display: 'flex', gap: 3 }}>
                                                        <button
                                                            title="Approve → discovered"
                                                            onClick={() => updateLeadStatus(l.id, 'discovered')}
                                                            style={{
                                                                width: 24, height: 24, borderRadius: 4, border: 'none',
                                                                background: '#22c55e', color: '#fff', cursor: 'pointer',
                                                                fontSize: 13, fontWeight: 700, lineHeight: '24px',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}
                                                        >✓</button>
                                                        <button
                                                            title="Reject → invalid"
                                                            onClick={() => updateLeadStatus(l.id, 'invalid')}
                                                            style={{
                                                                width: 24, height: 24, borderRadius: 4, border: 'none',
                                                                background: '#7f1d1d', color: '#fca5a5', cursor: 'pointer',
                                                                fontSize: 13, fontWeight: 700, lineHeight: '24px',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}
                                                        >✗</button>
                                                    </div>
                                                ) : ['invalid', 'bounced'].includes(l.status) ? (
                                                    <input type="checkbox" className="checkbox" disabled
                                                        title={`Cannot send: ${l.status}`}
                                                        style={{ opacity: 0.3 }} />
                                                ) : (
                                                    <input type="checkbox" className="checkbox"
                                                        checked={selectedIds.has(l.id)}
                                                        onChange={() => toggleSelect(l.id)} />
                                                )}
                                            </td>
                                            <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}
                                                onClick={() => selectLead(l)}>{l.name}</td>
                                            <td onClick={() => selectLead(l)}>{l.title}</td>
                                            <td onClick={() => selectLead(l)}>{l.companyName}</td>
                                            <td><a href={`mailto:${l.email}`} style={{ color: 'var(--accent-light)', textDecoration: 'none' }}
                                                onClick={(e) => e.stopPropagation()}>{l.email}</a></td>
                                            <td onClick={() => selectLead(l)}>{REGION_ICONS[l.country] || '🌍'} {l.country}</td>
                                            <td onClick={() => selectLead(l)}>
                                                {(() => {
                                                    const lang = getCountryLanguage(l.country); return (
                                                        <span style={{
                                                            fontSize: 11, padding: '2px 6px', borderRadius: 4,
                                                            background: isEnglishCountry(l.country) ? 'transparent' : 'rgba(168,85,247,0.15)',
                                                            color: isEnglishCountry(l.country) ? 'var(--text-muted)' : '#c084fc',
                                                        }}>
                                                            {isEnglishCountry(l.country) ? 'EN' : `${lang.nativeName}`}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td onClick={() => selectLead(l)} style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                                {campaigns.find(c => c.id === l.campaignId)?.name || '—'}
                                            </td>
                                            <td onClick={() => selectLead(l)}><span className={`badge badge-${l.status}`}>{l.status}</span></td>
                                            <td onClick={() => selectLead(l)}>{l.emailsSentCount}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Detail panel */}
                {selectedLead && (
                    <div className="card" style={{ position: 'sticky', top: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedLead.name}</div>
                                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{selectedLead.title}</div>
                                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedLead.companyName}</div>
                            </div>
                            <button className="btn btn-outline btn-sm" onClick={() => setSelectedLead(null)}>✕</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                            <div>
                                <div className="section-title">Email</div>
                                <a href={`mailto:${selectedLead.email}`} style={{ color: 'var(--accent-light)', fontSize: 13 }}>{selectedLead.email}</a>
                            </div>
                            <div>
                                <div className="section-title">Country</div>
                                <span style={{ fontSize: 13 }}>{REGION_ICONS[selectedLead.country] || '🌍'} {selectedLead.country}</span>
                            </div>
                            <div>
                                <div className="section-title">Status</div>
                                <span className={`badge badge-${selectedLead.status}`}>{selectedLead.status}</span>
                            </div>
                            <div>
                                <div className="section-title">Language</div>
                                {(() => {
                                    const lang = getCountryLanguage(selectedLead.country); return (
                                        <span style={{ fontSize: 13 }}>
                                            {lang.flag} {lang.nativeName}
                                            {!isEnglishCountry(selectedLead.country) && (
                                                <span style={{ fontSize: 10, color: '#f59e0b', marginLeft: 4 }}>🌐 auto-translate</span>
                                            )}
                                        </span>
                                    );
                                })()}
                            </div>
                            <div>
                                <div className="section-title">Emails sent</div>
                                <span style={{ fontSize: 13 }}>{selectedLead.emailsSentCount}</span>
                            </div>
                        </div>

                        {/* Review action buttons */}
                        {selectedLead.status === 'review' && (
                            <div style={{
                                display: 'flex', gap: 8, marginBottom: 16,
                                padding: '12px 16px', borderRadius: 'var(--radius-md)',
                                background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)',
                            }}>
                                <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600, flex: 1, lineHeight: '32px' }}>
                                    🔶 Needs review
                                </span>
                                <button
                                    className="btn btn-sm"
                                    style={{ background: '#22c55e', color: '#fff', fontWeight: 700, padding: '6px 16px' }}
                                    onClick={() => updateLeadStatus(selectedLead.id, 'discovered')}
                                >
                                    ✓ Approve
                                </button>
                                <button
                                    className="btn btn-sm"
                                    style={{ background: '#7f1d1d', color: '#fca5a5', fontWeight: 700, padding: '6px 16px' }}
                                    onClick={() => updateLeadStatus(selectedLead.id, 'invalid')}
                                >
                                    ✗ Reject
                                </button>
                            </div>
                        )}

                        {/* Quick send button */}
                        <button className="btn btn-primary btn-block" style={{ marginBottom: 16 }}
                            disabled={['invalid', 'review', 'bounced'].includes(selectedLead.status)}
                            onClick={() => {
                                setSelectedIds(new Set([selectedLead.id]));
                                setShowCompose(true);
                            }}>
                            ✉️ Send Email to {selectedLead.name}
                        </button>

                        <div className="section-title">Email History</div>
                        {threads.length === 0 ? (
                            <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>No messages yet</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
                                {threads.map(t => (
                                    <div key={t.id} style={{
                                        padding: '10px 12px',
                                        background: t.direction === 'outbound' ? 'var(--accent-bg)' : 'var(--bg-elevated)',
                                        borderRadius: 'var(--radius-md)', fontSize: 12,
                                    }}>
                                        <div style={{ color: 'var(--text-muted)', marginBottom: 4, fontSize: 10 }}>
                                            {t.direction === 'outbound' ? '→ Sent' : '← Received'} · {new Date(t.receivedAt).toLocaleString()}
                                        </div>
                                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{t.subject}</div>
                                        <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{t.bodyText.slice(0, 200)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ─── Compose & Auto-Send Modal ─── */}
            {showCompose && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !sending) setShowCompose(false); }}>
                    <div className="modal-panel" style={{ width: 720 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div>
                                <h2 style={{ fontSize: 18, fontWeight: 800 }}>
                                    ✉️ Auto-Send to {selectedLeads.length} Contact{selectedLeads.length > 1 ? 's' : ''}
                                </h2>
                                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                                    Email will be auto-personalized for each contact using placeholders
                                </p>
                                {/* Language badges */}
                                {(() => {
                                    const langs = getLanguagesForCountries(selectedLeads.map(l => l.country));
                                    const hasNonEnglish = langs.some(l => l.code !== 'en');
                                    return hasNonEnglish ? (
                                        <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>🌐 Auto-translate:</span>
                                            {langs.filter(l => l.code !== 'en').map(l => (
                                                <span key={l.code} style={{
                                                    fontSize: 10, padding: '2px 8px', borderRadius: 4,
                                                    background: 'rgba(168,85,247,0.15)', color: '#c084fc', fontWeight: 600,
                                                }}>
                                                    {l.flag} {l.nativeName}
                                                </span>
                                            ))}
                                            <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                                                (write in English → auto-translated per country)
                                            </span>
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                            {!sending && <button className="btn btn-outline btn-sm" onClick={() => setShowCompose(false)}>✕</button>}
                        </div>

                        {/* Recipients preview */}
                        <div style={{ marginBottom: 16 }}>
                            <label className="section-title">Recipients ({selectedLeads.length})</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 80, overflowY: 'auto' }}>
                                {selectedLeads.map(l => (
                                    <span key={l.id} style={{
                                        padding: '3px 10px', borderRadius: 'var(--radius-pill)',
                                        background: 'var(--accent-bg)', color: 'var(--accent-light)',
                                        fontSize: 11, fontWeight: 600,
                                    }}>
                                        {l.name} · {l.companyName} · &lt;{l.email}&gt;
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* From — region-locked */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">
                                From Account
                                {commonRegion && (
                                    <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--accent-light)', fontWeight: 400 }}>
                                        🔒 Region-locked to {getRegionLabel(commonRegion)}
                                    </span>
                                )}
                            </label>
                            <select className="input" value={effectiveFromEmail} onChange={(e) => setFromEmail(e.target.value)}>
                                {allowedSenders.map(s => (
                                    <option key={s.email} value={s.email}>{s.displayName} &lt;{s.email}&gt;</option>
                                ))}
                            </select>
                        </div>

                        {/* Subject */}
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">Subject</label>
                            <input className="input" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                                placeholder="e.g. Quick opportunity for {{name}} at {{company}}" />
                        </div>

                        {/* Body */}
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label className="section-title">Body</label>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {selectedLeads.length > 0 && (
                                        <button className="btn btn-outline btn-sm"
                                            onClick={() => { setShowPreview(!showPreview); setPreviewIndex(0); }}>
                                            {showPreview ? '✏️ Edit' : `👁️ Preview (${selectedLeads.length})`}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {showPreview && selectedLeads.length > 0 ? (
                                <div>
                                    {/* Preview navigation */}
                                    <div style={{
                                        padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                                        marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    }}>
                                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                            Previewing: <strong>{selectedLeads[previewIndex]?.name}</strong> @ {selectedLeads[previewIndex]?.companyName}
                                            {' '}({previewIndex + 1}/{selectedLeads.length})
                                        </span>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <button className="btn btn-outline btn-sm"
                                                disabled={previewIndex === 0}
                                                onClick={() => setPreviewIndex(i => i - 1)}>◀</button>
                                            <button className="btn btn-outline btn-sm"
                                                disabled={previewIndex === selectedLeads.length - 1}
                                                onClick={() => setPreviewIndex(i => i + 1)}>▶</button>
                                        </div>
                                    </div>
                                    {/* Preview subject */}
                                    <div style={{
                                        padding: '8px 12px', background: 'var(--bg-input)', border: '1px solid var(--border-default)',
                                        borderRadius: 'var(--radius-sm)', marginBottom: 4, fontSize: 13, color: 'var(--text-primary)',
                                    }}>
                                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Subject: </span>
                                        {renderTemplate(emailSubject, getPreviewVars(selectedLeads[previewIndex]))}
                                    </div>
                                    {/* Preview body */}
                                    <div style={{
                                        padding: 16, background: 'var(--bg-input)', border: '1px solid var(--border-default)',
                                        borderRadius: 'var(--radius-sm)', fontSize: 13, whiteSpace: 'pre-wrap',
                                        color: 'var(--text-primary)', minHeight: 160,
                                    }}>
                                        {renderTemplate(emailBody, getPreviewVars(selectedLeads[previewIndex]))}
                                    </div>
                                </div>
                            ) : (
                                <textarea className="input" value={emailBody} onChange={(e) => setEmailBody(e.target.value)}
                                    rows={8} placeholder="Hi {{name}},&#10;&#10;I came across {{company}} and..." />
                            )}
                        </div>

                        {/* Placeholder tags */}
                        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {PLACEHOLDERS.map(p => (
                                <span key={p} style={{
                                    padding: '2px 8px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                                    fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer',
                                }}
                                    onClick={() => setEmailBody(prev => prev + p)}
                                    title={`Click to insert ${p}`}
                                >
                                    {p}
                                </span>
                            ))}
                        </div>

                        {/* Sending progress */}
                        {sending && (
                            <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>📤 Sending...</span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-light)' }}>
                                        {sendProgress.sent} / {sendProgress.total}
                                    </span>
                                </div>
                                <div className="progress-bar" style={{ height: 6 }}>
                                    <div className="progress-fill" style={{
                                        width: `${sendProgress.total > 0 ? sendProgress.sent / sendProgress.total * 100 : 0}%`,
                                        background: 'var(--success)',
                                    }} />
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            {!sending && <button className="btn btn-outline" onClick={() => setShowCompose(false)}>Cancel</button>}
                            <button className="btn btn-success btn-lg" onClick={autoSend}
                                disabled={sending || !fromEmail || !emailSubject || !emailBody}>
                                {sending
                                    ? `Sending ${sendProgress.sent}/${sendProgress.total}...`
                                    : `📤 Auto-Send to ${selectedLeads.length} Contact${selectedLeads.length > 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
